import {
  BookOpen,
  FlaskConical,
  Globe,
  GraduationCap,
  Lightbulb,
  Rocket,
  Star,
  Trophy,
  type LucideIcon,
} from 'lucide-react';

import type { ClassCategory } from '@/types/instituteClass';

/**
 * The fixed sets a class is described from.
 *
 * Categories and boards mirror the server's enums; icons and colours are keys
 * the *client* owns — the database stores `"rocket"` and `"violet"`, never a
 * component name or a hex, so a rebrand changes this file and no rows.
 */

export const CLASS_CATEGORY_LABEL: Record<ClassCategory, string> = {
  PRE_PRIMARY: 'Pre-Primary',
  PRIMARY: 'Primary',
  MIDDLE: 'Middle',
  SECONDARY: 'Secondary',
  HIGHER_SECONDARY: 'Higher Secondary',
};

export const CLASS_CATEGORY_OPTIONS = [
  // Blank is a real answer: it means "work it out from the name", which is what
  // the product did for every class set up before this field existed.
  { value: '', label: 'Work it out from the name' },
  ...(Object.entries(CLASS_CATEGORY_LABEL) as [ClassCategory, string][]).map(([value, label]) => ({
    value,
    label,
  })),
];

/** The icons a class may be recognised by. The key is what is stored. */
export const CLASS_ICONS: { key: string; label: string; icon: LucideIcon }[] = [
  { key: 'graduation', label: 'Graduation cap', icon: GraduationCap },
  { key: 'book', label: 'Book', icon: BookOpen },
  { key: 'star', label: 'Star', icon: Star },
  { key: 'rocket', label: 'Rocket', icon: Rocket },
  { key: 'flask', label: 'Flask', icon: FlaskConical },
  { key: 'globe', label: 'Globe', icon: Globe },
  { key: 'trophy', label: 'Trophy', icon: Trophy },
  { key: 'idea', label: 'Lightbulb', icon: Lightbulb },
];

export const DEFAULT_CLASS_ICON = CLASS_ICONS[0];

/** Resolves a stored key, falling back rather than drawing nothing. */
export function classIcon(key: string | null | undefined): LucideIcon {
  return CLASS_ICONS.find((entry) => entry.key === key)?.icon ?? DEFAULT_CLASS_ICON.icon;
}

/**
 * The colours a class may be tinted with.
 *
 * `swatch` paints the chooser and the class's own mark; `soft` is the quiet
 * version for a chip or a bar. Written as literal utilities rather than tokens
 * because these are a palette to pick *from* — there is no single "class
 * colour" for a token to hold.
 */
export const CLASS_COLOURS: { key: string; label: string; swatch: string; soft: string }[] = [
  { key: 'blue', label: 'Blue', swatch: 'bg-blue-500', soft: 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300' },
  { key: 'indigo', label: 'Indigo', swatch: 'bg-indigo-500', soft: 'bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300' },
  { key: 'violet', label: 'Violet', swatch: 'bg-violet-500', soft: 'bg-violet-100 dark:bg-violet-950/60 text-violet-700 dark:text-violet-300' },
  { key: 'pink', label: 'Pink', swatch: 'bg-pink-500', soft: 'bg-pink-100 dark:bg-pink-950/60 text-pink-700 dark:text-pink-300' },
  { key: 'orange', label: 'Orange', swatch: 'bg-orange-500', soft: 'bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-300' },
  { key: 'amber', label: 'Amber', swatch: 'bg-amber-500', soft: 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300' },
  { key: 'emerald', label: 'Emerald', swatch: 'bg-emerald-500', soft: 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300' },
  { key: 'cyan', label: 'Cyan', swatch: 'bg-cyan-500', soft: 'bg-cyan-100 dark:bg-cyan-950/60 text-cyan-700 dark:text-cyan-300' },
];

export const DEFAULT_CLASS_COLOUR = CLASS_COLOURS[0];

/** Resolves a stored key, falling back rather than drawing an untinted mark. */
export function classColour(key: string | null | undefined) {
  return CLASS_COLOURS.find((entry) => entry.key === key) ?? DEFAULT_CLASS_COLOUR;
}
